import { FormProvider, useWatch } from "react-hook-form";
import { PageMode } from "../../../../common/types";
import styles from "./StudioWizard.module.scss";
import { SingleSectionPageLayout } from "../../../../common/components/common/SingleSectionPageLayout";
import { useStudioWizardPage } from "./hooks/useStudioWizardPage";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultTextareaField } from "../../../../common/components/forms/inputs/textareaField/DefaultTextareaField";
import { nameof } from "ts-simple-nameof";
import {
  IStudioWizardFormModel,
  MAX_EMAIL_SENDER_NAME_LENGTH,
} from "./models/studioWizardFormModel";
import { IconButton } from "../../../../common/components/button/iconButton/IconButton";
import { IconType } from "../../../../common/icons/Icon";
import { MAX_RESERVATION_NOTIFICATION_EMAILS } from "./models/reservationNotificationEmails";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultFormSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { HiddenField } from "../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { DefaultPhoneInputField } from "../../../../common/components/forms/inputs/phoneField/DefaultPhoneInputField";
import { generateHexFromString } from "../../../../common/infrastructure/utilities/colorUtility";
import { useState } from "react";
import { WorkingHoursDialog } from "../../../../common/components/common/workingHoursDialog/WorkingHoursDialog";
import { MessageBanner } from "../../../../common/components/common/MessageBanner/MessageBanner";
import { SectionDivider } from "../../../../common/components/common/SectionDivider/SectionDivider";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { DefaultFormMultiSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormMultiSelectField";
import { CurrencyType } from "../../../../common/enums";
import { Accordion, AccordionTab } from "primereact/accordion";
import { DefaultToggle } from "../../../../common/components/forms/inputs/toggle/DefaultToggle";
import { StudioQrCodesSection } from "./qrCodes/StudioQrCodesSection";
import { StudioLinkPageSection } from "./linkPage/StudioLinkPageSection";
import { StudioLogoField } from "./logo/StudioLogoField";
import { DesignStudioEntry } from "./designStudio/components/DesignStudioEntry";

import { dvSize } from "../../../../common/infrastructure/utilities/viewportUnitsUtility";
interface IStudioWizard {
  pageMode: PageMode;
}

export const StudioWizard = ({ pageMode }: IStudioWizard) => {
  const {
    id,
    form,
    pageTitle,
    tenantOptions: ownerOptions,
    isLoading,
    actionButtonLabel,
    canSelectOwner,
    isDesktopOrTablet,
    workingHoursData,
    studioTypeOptions,
    timeStepOptions,
    commissionsVisibilityOptions,
    isGlobalAdmin,
    timezoneNames,
    paymentOptions,
    hiddenPaymentTypes,
    isMultisportAvailable,
    isCoolfitAvailable,
    showGroupStudioTypeOptions,
    showServiceStudioTypeOptions,
    showGymStudioTypeOptions,
    showStoreStudioTypeOptions,
    usersCanCancelReservations,
    allowLateCancellationsWithPayment,
    studioDoesNotUsePlans,
    logoUrl,
    reservationNotificationEmails,
    canUseDeviceNotifications,
    onSave,
  } = useStudioWizardPage(pageMode);

  const studioName = form.watch("name");
  const buttonBackgroundColor = form.watch("buttonBackgroundColor");
  const buttonTextColor = form.watch("buttonTextColor");
  const useGroupClassCategories = form.watch("useGroupClassCategories");
  const smsContent = useWatch({
    control: form.control,
    name: "smsReminderContent",
  });
  const smsCharCount = (smsContent ?? "").length;
  const width = isDesktopOrTablet ? "600px" : dvSize("80dvw");
  const [showWorkingTimeDialog, setShowWorkingTimeDialog] = useState(false);
  const isGymOnly =
    showGymStudioTypeOptions &&
    !showGroupStudioTypeOptions &&
    !showServiceStudioTypeOptions &&
    !showStoreStudioTypeOptions;
  const isStoreOnly =
    showStoreStudioTypeOptions &&
    !showGroupStudioTypeOptions &&
    !showServiceStudioTypeOptions &&
    !showGymStudioTypeOptions;

  const children = (
    <>
      <div className={styles.root_fields}>
        <HiddenField name={nameof<IStudioWizardFormModel>((x) => x.id)} />

        <Accordion multiple activeIndex={[0]} className={styles.accordion}>
          {/* Section 1: Basic Information */}
          <AccordionTab header="Основна информация">
            <div className={styles.accordion_section}>
              <DefaultInputField
                name={nameof<IStudioWizardFormModel>((x) => x.name)}
                label="Име на обекта"
                placeholder="Pilates Reform"
                width={width}
                required
              />

              {/* Sits here, next to the name it overrides, rather than by the
                  notification emails: that section is gated behind
                  !isGymOnly && !isStoreOnly, which would hide this field from
                  gym-only and store-only studios entirely. */}
              <DefaultInputField
                name={nameof<IStudioWizardFormModel>((x) => x.emailSenderName)}
                label="Име на подателя в имейлите"
                placeholder="Pilates Reform"
                width={width}
                tooltip={`Името, което клиентите виждат като подател на имейлите от обекта. Оставете празно за да се използва името на обекта. Максимум ${MAX_EMAIL_SENDER_NAME_LENGTH} символа. Кирилицата заема около два пъти повече място в имейл хедъра, затова дълго кирилско име се изписва на латиница при изпращане.`}
              />

              <DefaultPhoneInputField
                name={nameof<IStudioWizardFormModel>((x) => x.phoneNumber)}
                label="Телефонен номер (задължителен)"
                required
                width={width}
              />

              <DefaultInputField
                name={nameof<IStudioWizardFormModel>(
                  (x) => x.phoneNumberDescription,
                )}
                label="Описание на телефонен номер"
                placeholder="напр. Иван Иванов, Рецепция"
                width={width}
              />

              <DefaultPhoneInputField
                name={nameof<IStudioWizardFormModel>(
                  (x) => x.secondaryPhoneNumber,
                )}
                label="Допълнителен телефонен номер"
                width={width}
              />

              <DefaultInputField
                name={nameof<IStudioWizardFormModel>(
                  (x) => x.secondaryPhoneNumberDescription,
                )}
                label="Описание на допълнителен телефонен номер"
                placeholder="напр. Мария Петрова, Управител"
                width={width}
              />

              <DefaultInputField
                name={nameof<IStudioWizardFormModel>((x) => x.email)}
                label="Имейл"
                placeholder="email@example.com"
                width={width}
                required
              />

              <DefaultInputField
                name={nameof<IStudioWizardFormModel>((x) => x.url)}
                label="Домейн"
                placeholder="https://portal.reform.com, https://varna.reform.com"
                width={width}
                disabled={!canSelectOwner}
                required
              />

              <DefaultInputField
                name={nameof<IStudioWizardFormModel>((x) => x.address)}
                label="Адрес на обекта"
                placeholder="Варна, ул. Княз Борис I, зала 2"
                width={width}
                required
              />

              <DefaultInputField
                name={nameof<IStudioWizardFormModel>((x) => x.addressMapUrl)}
                label="Линк към локация (Google Maps)"
                placeholder="https://maps.app.goo.gl/..."
                width={width}
                tooltip="Точният линк към локацията на обекта. Отворете обекта в Google Maps, натиснете 'Сподели' и поставете линка тук. Когато е попълнен, клик върху адреса в портала и в Link страницата отваря точно тази локация. Ако го оставите празно, се търси по адреса по-горе - което не работи, ако в адреса има пояснения (например 'вход откъм двора')."
              />

              <RenderWhen condition={canSelectOwner}>
                <DefaultFormSelectField
                  isLoading={isLoading}
                  label="Собственик"
                  name={nameof<IStudioWizardFormModel>((x) => x.ownerId)}
                  options={ownerOptions}
                  disabled={!!id}
                  width={width}
                  required
                />
              </RenderWhen>

              <RenderWhen condition={isGlobalAdmin}>
                <DefaultFormMultiSelectField
                  name={nameof<IStudioWizardFormModel>((x) => x.types)}
                  label="Типове на обекта"
                  options={studioTypeOptions}
                  includeId={false}
                  required
                />

                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>((x) => x.isTraining)}
                  label="Това е тренировъчен обект"
                />
              </RenderWhen>

              <RenderWhen condition={!isStoreOnly}>
                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.studioDoesNotUsePlans,
                  )}
                  label="Обектът не използва планове"
                  tooltip="Когато тази опция е активирана, всичко свързано с плановете се скрива: менютата „Планове“, „Типове планове“ и „Зареждане на карта“, плановете в профила на клиент и на семейство, „Моите планове“ при клиентите и начинът на плащане „Абонаментна карта“ в секция „Плащания“ (той остава активен за вече продадените карти). Вече създадените планове и карти не се изтриват - щом изключите опцията, всичко се появява отново."
                />
              </RenderWhen>

              <RenderWhen condition={!isStoreOnly && !studioDoesNotUsePlans}>
                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.studioUsesDifferentPlanTypes,
                  )}
                  label="Обектът използва повече от един вид абонаментен план"
                  tooltip="Когато тази опция е активирана, обекта може да добави още едно ниво на абстракция към плановете си и да позволи един потребител да има повече от един абонаментен план, като всеки абонаментен план има свой тип. Ограничението е: един потребител може да има повече от един абонаментен план, но всеки един такъв трябва да е от различен тип."
                />
              </RenderWhen>

              <RenderWhen condition={!isStoreOnly}>
                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.usesPreReservationForms,
                  )}
                  label="Обектът използва формуляри"
                  tooltip="Когато тази опция е активирана, обектът може да създава формуляри от меню Администрация → Други → Форми и да ги използва по два начина: клиентите ги попълват и приемат преди резервация за събитие или услуга, за която е избран формуляр, а служителите могат да изпратят формуляр на клиент по имейл от профила му, за да го попълни самостоятелно, без резервация. Попълнените формуляри се виждат в профила на клиента."
                />
              </RenderWhen>

              <RenderWhen condition={!isStoreOnly}>
                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.studioDoesNotUseWorkouts,
                  )}
                  label="Обектът не използва тренировки"
                  tooltip="Когато тази опция е активирана, „Моите тренировки“ се скрива от менюто на клиентите. Служителите продължават да виждат и създават тренировки."
                />
              </RenderWhen>

              <RenderWhen condition={!isStoreOnly}>
                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.studioDoesNotUseBodyMetrics,
                  )}
                  label="Обектът не използва метрики"
                  tooltip="Когато тази опция е активирана, „Моите метрики“ се скрива от менюто на клиентите, а редът „Метрики“ - от профила на клиента."
                />
              </RenderWhen>

              <RenderWhen condition={!isStoreOnly}>
                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.studioDoesNotUseGoals,
                  )}
                  label="Обектът не използва цели"
                  tooltip="Когато тази опция е активирана, „Моите цели“ се скрива от менюто на клиентите, а редът „Цели“ - от профила на клиента."
                />
              </RenderWhen>

              <RenderWhen condition={showServiceStudioTypeOptions}>
                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.studioDoesNotUseServicePortfolios,
                  )}
                  label="Обектът не използва портфолиа"
                  tooltip="Когато тази опция е активирана, портфолиото на служителите се скрива: полето за снимки във формата на услуга и бутонът „Виж портфолио“ при избор на специалист в портала. Вече качените снимки не се изтриват - щом изключите опцията, те се появяват отново."
                />
              </RenderWhen>

              <DefaultToggle
                name={nameof<IStudioWizardFormModel>(
                  (x) => x.disableGoogleLogin,
                )}
                label="Деактивирай вход с Google"
                tooltip="Когато тази опция е активирана, бутонът 'Вход с Google' се скрива от страницата за вход на обекта. Потребители, които досега са влизали само с Google, трябва да въведат телефонния си номер - системата ще ги подкани да си зададат парола. Алтернативно могат да възстановят паролата си през 'Забравена парола' или с линк за задаване на парола, генериран от обекта."
              />

              <RenderWhen condition={showServiceStudioTypeOptions}>
                <DefaultFormSelectField
                  name={nameof<IStudioWizardFormModel>((x) => x.timeStep)}
                  label="Времева стъпка"
                  tooltip="Интервал между свободните часове за резервация. При 30 мин: 9:00, 9:30, 10:00. При 1 час: 9:00, 10:00, 11:00."
                  options={timeStepOptions}
                  includeId={false}
                />
              </RenderWhen>

              <DefaultFormSelectField
                label="Часова зона на обекта"
                name={nameof<IStudioWizardFormModel>((x) => x.timezone)}
                options={timezoneNames}
                width={width}
                required
              />
            </div>
          </AccordionTab>
          {/* Section 2: Branding */}
          <AccordionTab header="Design Studio">
            <div className={styles.accordion_section}>
              {pageMode === "add" || !id ? (
                <MessageBanner
                  type="info"
                  width={width}
                  message="Лого може да бъде качено след като обектът е вече създаден."
                />
              ) : (
                <StudioLogoField
                  studioId={Number(id)}
                  studioName={studioName}
                  logoUrl={logoUrl}
                  width={width}
                />
              )}

              <DesignStudioEntry
                form={form}
                studioName={studioName}
                width={width}
              />
            </div>
          </AccordionTab>

          {/* Section 3: Scheduling & Timing */}
          {!isGymOnly && !isStoreOnly && (
            <AccordionTab header="График и времена">
              <div className={styles.accordion_section}>
                <RenderWhen condition={usersCanCancelReservations}>
                  <DefaultInputField
                    name={nameof<IStudioWizardFormModel>(
                      (x) => x.minutesBeforeEventCancellationCloses,
                    )}
                    label="Краен срок за отказ на резервация (минути преди започване)"
                    placeholder="120"
                    type="number"
                    width={width}
                    tooltip="Определя колко време преди началото на събитието потребителите могат да откажат своята резервация. Например, при 120 минути, ако събитието започва в 14:00, последният момент за отказ е 12:00."
                  />
                </RenderWhen>

                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.minutesAfterEventToMarkAsMissed,
                  )}
                  label="След колко минути от началото се маркира като 'неявил се'"
                  placeholder="15"
                  type="number"
                  width={width}
                  tooltip="Времето след началото на събитието, след което резервацията автоматично се маркира като 'неявил се'. Например при 15 минути, ако събитието започва в 14:00, резервацията ще се маркира в 14:15."
                />

                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.reservationCloseTimeBeforeEventMinutes,
                  )}
                  label="Краен срок за нова резервация (минути преди започване)"
                  placeholder="60"
                  type="number"
                  width={width}
                  tooltip="Определя колко време преди началото потребителите могат да правят нови резервации от портала. Например при 60 минути, ако събитието или свободния слот е 14:00ч, последният момент за резервация е 13:00ч."
                />

                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.scheduleVisibilityDaysAhead,
                  )}
                  label="Видимост на графика за обикновени потребители (дни напред)"
                  placeholder="14"
                  type="number"
                  width={width}
                  tooltip="Колко дни напред обикновените потребители могат да виждат и правят резервации в календара. Например при 14 дни, ако днес е 1-ви януари, потребителите ще виждат събития до 15-ти януари."
                />

                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.scheduleVisibilityDaysAheadForSubscribedUsers,
                  )}
                  label="Видимост на графика за потребители с абонамент (дни напред)"
                  placeholder="30"
                  type="number"
                  width={width}
                  tooltip="Колко дни напред потребителите с активен абонамент могат да виждат и резервират събития. Обикновено е по-дълъг период от този за обикновените потребители, за да имат предимство при ранно записване."
                />
              </div>
            </AccordionTab>
          )}

          {/* Section 4: Class Settings */}
          {showGroupStudioTypeOptions && (
            <AccordionTab header="Настройки за класове">
              <div className={styles.accordion_section}>
                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>((x) => x.eventBasePrice)}
                  label="Базова цена за клас"
                  placeholder="15"
                  type="number"
                  currency={CurrencyType.EUR}
                  width={width}
                />

                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.eventBaseFreeSpaces,
                  )}
                  label="Капацитет по подразбиране (максимален брой участници)"
                  placeholder="15"
                  type="number"
                  width={width}
                  tooltip="Максималният брой участници, който автоматично се попълва при създаване на ново събитие. Може да бъде променян индивидуално за всяко събитие."
                />

                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.showAvailableSeatsInPortal,
                  )}
                  label="Показвай броя свободни места в календара на потребителите"
                />

                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.displayClosedReservationsLabel,
                  )}
                  label="Показване на 'Затворени резервации' при изтекъл срок за записване"
                  tooltip="Работи заедно с настройката 'Краен срок за нова резервация'. Когато срокът за записване изтече (напр. 60 мин преди класа), потребителите ще виждат етикет 'Затворени резервации'. При изключена опция ще се показва стандартният етикет за свободни места."
                />

                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.useGroupClassCategories,
                  )}
                  label="Използвай избор от категории за групови класове вместо календар"
                  tooltip="Потребителите ще избират класове по категории (подобно на резервация за услуга) вместо да разглеждат календара ден по ден. Позволява организиране на класовете в категории за по-лесна навигация."
                />

                <RenderWhen condition={useGroupClassCategories}>
                  <DefaultToggle
                    name={nameof<IStudioWizardFormModel>(
                      (x) => x.showCalendarTileInPortal,
                    )}
                    label="Покажи 'Календар' на началната страница на портала"
                    tooltip="Когато е активирано, на началната страница на портала все пак ще се показва опцията 'Календар', която пренасочва към стария изглед с календар. Полезно при преход към новия режим с категории."
                  />
                </RenderWhen>
              </div>
            </AccordionTab>
          )}
          {/* Section 5: Features */}
          {!isGymOnly && !isStoreOnly && (
            <AccordionTab header="Функционалности">
              <div className={styles.accordion_section}>
                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.usersCanCancelReservations,
                  )}
                  label="Да могат ли потребителите да отказват резервации?"
                />

                <RenderWhen condition={usersCanCancelReservations}>
                  <DefaultToggle
                    name={nameof<IStudioWizardFormModel>(
                      (x) => x.allowLateCancellationsWithPayment,
                    )}
                    label="Позволи късни отказвания с такса"
                    tooltip="Когато е активирано, потребителите могат да откажат резервации след изтичане на крайния срок, но ще трябва да заплатят такса. Акаунтът им ще бъде ограничен докато не заплатят."
                  />
                </RenderWhen>

                <RenderWhen
                  condition={
                    usersCanCancelReservations &&
                    allowLateCancellationsWithPayment
                  }
                >
                  <DefaultToggle
                    name={nameof<IStudioWizardFormModel>(
                      (x) => x.autoMarkUserAsUnreliableOnLateCancellation,
                    )}
                    label="Автоматично маркирай потребител като нередовен при късно отказване"
                    tooltip="Когато е активирано, потребителите които откажат резервация след крайния срок автоматично ще бъдат маркирани като нередовни. Можете да ги маркирате като редовни по всяко време от техният профил."
                  />
                </RenderWhen>

                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.askForUserBirthdayOnRegistration,
                  )}
                  label="Събиране на рождена дата при регистрация"
                  tooltip="При активирана опция, полето за рождена дата ще се показва във формата за регистрация. Полето е незадължително за попълване от потребителите."
                />

                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>((x) => x.usesHalls)}
                  label="Обектът има повече от една зала"
                  tooltip="При активирана опция, при създаване на събития ще може да се избира конкретна зала. Полезно за обекти с повече от едно помещение за провеждане на дейности."
                />

                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.limitUserToOneReservationPerDay,
                  )}
                  label="Не позволявай на един потребител да направи повече от една резервация за един и същ ден"
                  tooltip="Когато тази опция е активирана, един потребител няма да може да направи повече от една резервация за един и същ ден. Ако искате логиката да е споделена между няколко свързани обекта, всеки от обектите трябва да я има активирана. Ако потребител е част от семейство и е настроен като отговорник, ще може да направи по една резервация на ден за всеки от членовете на семейството, от тяхно име."
                />

                <DefaultToggle
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.enableQuickArrival,
                  )}
                  label="Бързо отбелязване на присъствие"
                  tooltip="До бутона за пристигане на всеки потвърден и още неплатен клиент се появява втори бутон (мълния), който отбелязва присъствието с едно докосване: с активната карта на клиента за този клас (ако „Абонаментна карта“ е сред начините на плащане на обекта), а иначе - с първия начин на плащане от списъка на обекта и цената на класа. След това за няколко секунди може да го отмените. Депозит, Multisport и Stripe никога не се избират автоматично."
                />
              </div>
            </AccordionTab>
          )}
          {/* Section 6: Reminders */}
          {!isGymOnly && !isStoreOnly && (
            <AccordionTab header="Напомняния">
              <div className={styles.accordion_section}>
                <SectionDivider label="Имейл" width={width} />

                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.firstReminderHoursBefore,
                  )}
                  label="Първо напомняне за резервация (часове преди събитието)"
                  placeholder="48"
                  type="number"
                  width={width}
                  tooltip="Колко часа преди началото на събитието да се изпрати първото напомняне. Оставете празно за да го деактивирате."
                />

                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.secondReminderHoursBefore,
                  )}
                  label="Второ напомняне за резервация (часове преди събитието)"
                  placeholder="3"
                  type="number"
                  width={width}
                  tooltip="Колко часа преди началото на събитието да се изпрати второто напомняне. Оставете празно за да го деактивирате. Трябва да е по-малко от първото напомняне."
                />

                {reservationNotificationEmails.emails.map((_, index) => (
                  <div
                    key={index}
                    className={styles.email_row}
                    style={{ width: width }}
                  >
                    <DefaultInputField
                      name={`${reservationNotificationEmails.fieldName}.${index}`}
                      label={`Имейл за известия при нови резервации${
                        reservationNotificationEmails.emails.length > 1
                          ? ` ${index + 1}`
                          : ""
                      }`}
                      placeholder="notifications@example.com"
                      width="100%"
                      // Not "off": Chrome ignores that for anything it classifies as an
                      // email field, and its autofill "refill" then copies the address
                      // just picked from the dropdown into a row added within ~1s.
                      // "new-password" is the one value Chrome reliably treats as
                      // "leave this field alone" on a text input.
                      autocomplete="new-password"
                      tooltip={
                        index === 0
                          ? `Имейл адресите, на които ще се изпращат известия за всяка нова резервация от портала, във всички часове на обекта. Може да добавите до ${MAX_RESERVATION_NOTIFICATION_EMAILS} адреса. Оставете празно за да деактивирате тази функция. Служителите могат сами да включат известия само за своите часове от „Моят профил → Известия“.`
                          : undefined
                      }
                    />
                    <RenderWhen
                      condition={reservationNotificationEmails.canRemoveEmail}
                    >
                      <IconButton
                        iconType={IconType.DeleteIcon2}
                        iconWidth="20px"
                        iconHeight="20px"
                        tooltip="Премахни имейл"
                        onClick={() =>
                          reservationNotificationEmails.removeEmail(index)
                        }
                      />
                    </RenderWhen>
                  </div>
                ))}

                <DefaultButton
                  label="Добави имейл"
                  design="outline"
                  type="button"
                  width={width}
                  disabled={!reservationNotificationEmails.canAddEmail}
                  onClick={reservationNotificationEmails.addEmail}
                />

                <SectionDivider label="SMS" width={width} />

                <DefaultInputField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.smsReminderHoursBefore,
                  )}
                  label="SMS напомняне (часове преди събитието)"
                  placeholder="2"
                  type="number"
                  width={width}
                  tooltip="SMS напомняне ще се изпрати на всеки потребител преди събитието. Ако е оставено празно, SMS напомнянията са изключени. Цена: €0.098 на SMS."
                />

                <MessageBanner
                  type="info"
                  width={width}
                  className={styles.sms_banner}
                  message="Всеки изпратен SMS се таксува по €0.10."
                />

                <DefaultTextareaField
                  name={nameof<IStudioWizardFormModel>(
                    (x) => x.smsReminderContent,
                  )}
                  label="SMS съдържание"
                  placeholder="Ще ви очакваме на {{date}} от {{time}}ч"
                  rows={3}
                  width={width}
                  tooltip="Използвайте {{date}} и {{time}} като заместители за дата и час."
                />
                <div
                  style={{
                    width,
                    textAlign: "right",
                    fontSize: "0.8rem",
                    color:
                      smsCharCount > 70
                        ? "rgb(255, 64, 83)"
                        : "rgba(255, 255, 255, 0.7)",
                    marginTop: "-8px",
                  }}
                >
                  {smsCharCount}/70
                </div>

                <RenderWhen condition={canUseDeviceNotifications}>
                  <SectionDivider label="Известие на устройството" width={width} />

                  <DefaultInputField
                    name={nameof<IStudioWizardFormModel>(
                      (x) => x.pushReminderHoursBefore,
                    )}
                    label="Известие на устройството (часове преди събитието)"
                    placeholder="2"
                    type="number"
                    width={width}
                    tooltip="Известие на устройствата на всеки клиент с потвърдена резервация, който е разрешил известията. Ако е оставено празно, напомнянето е изключено. Безплатно."
                  />

                  <MessageBanner
                    type="info"
                    width={width}
                    className={styles.sms_banner}
                    message="Известието стига само до клиенти, които са разрешили известията на устройството си. Дали ще го видят зависи и от настройките на телефона или компютъра им (например режим „Не безпокойте“ или изключени известия)."
                  />
                </RenderWhen>
              </div>
            </AccordionTab>
          )}
          {/* Section 7: Payments */}
          <AccordionTab header="Плащания">
            <div className={styles.accordion_section}>
              <DefaultFormMultiSelectField
                label="Поддържани начини на плащане"
                name={nameof<IStudioWizardFormModel>(
                  (x) => x.supportedTypesOfPayment,
                )}
                options={paymentOptions}
                hiddenValues={hiddenPaymentTypes}
                width={width}
                required
                includeId={false}
              />

              <DefaultFormSelectField
                name={nameof<IStudioWizardFormModel>(
                  (x) => x.commissionsVisibleIn,
                )}
                label="Показвай комисионните на служителите в"
                tooltip="Някои обекти изчисляват комисионните на служителите си от репорта по приходи, други — от репорта по посещения. Изберете къде да се показват, за да не се бъркат. В скрития репорт не се показват нито комисионните, нито редът 'Печалба за обекта'. Ако обектът не използва комисионни, изберете 'Никъде' — тогава се скриват и комисионните проценти при служителите."
                options={commissionsVisibilityOptions}
                width={width}
                includeId={false}
              />

              <RenderWhen condition={showGroupStudioTypeOptions}>
                <RenderWhen condition={!!isMultisportAvailable}>
                  <DefaultInputField
                    name={nameof<IStudioWizardFormModel>(
                      (x) => x.percentLeftAfterMultisportTaxes,
                    )}
                    label="Процент печалба при плащане с MultiSport"
                    placeholder="80"
                    type="number"
                    width={width}
                    tooltip="Процентът, който остава като печалба след приспадане на таксите на MultiSport. Например при 80%, ако базовата цена е 20 лв, реалната печалба ще бъде 16 лв (20 лв × 80%). Използва се за статистически цели."
                  />
                </RenderWhen>

                <RenderWhen condition={!!isCoolfitAvailable}>
                  <DefaultInputField
                    name={nameof<IStudioWizardFormModel>(
                      (x) => x.percentLeftAfterCoolFitTaxes,
                    )}
                    label="Процент печалба при плащане с CoolFit"
                    placeholder="80"
                    type="number"
                    width={width}
                    tooltip="Процентът, който остава като печалба след приспадане на таксите на CoolFit. Например при 80%, ако базовата цена е 20 лв, реалната печалба ще бъде 16 лв (20 лв × 80%). Използва се за статистически цели."
                  />
                </RenderWhen>
              </RenderWhen>
            </div>
          </AccordionTab>
          {/* Section 8: Working Hours */}
          <AccordionTab header="Работно време">
            <div className={styles.accordion_section}>
              <DefaultButton
                label="Управление на работно време"
                tooltip={
                  pageMode === "add"
                    ? "Работното време може да бъде зададено след като обекта е вече създаден."
                    : undefined
                }
                disabled={pageMode === "add"}
                design="outline"
                width={width}
                onClick={() => setShowWorkingTimeDialog(true)}
              />
            </div>
          </AccordionTab>
          {/* Section 9: QR codes */}
          <AccordionTab header="QR кодове">
            <div className={styles.accordion_section}>
              {pageMode === "add" || !id ? (
                <MessageBanner
                  type="info"
                  width={width}
                  message="QR кодове могат да бъдат създадени след като обектът е вече създаден."
                />
              ) : (
                <StudioQrCodesSection studioId={Number(id)} />
              )}
            </div>
          </AccordionTab>
          {/* Section 10: Link page */}
          <AccordionTab header="Линк страница">
            <div className={styles.accordion_section}>
              {pageMode === "add" || !id ? (
                <MessageBanner
                  type="info"
                  width={width}
                  message="Линк страницата може да бъде създадена след като обектът е вече създаден."
                />
              ) : (
                <StudioLinkPageSection studioId={Number(id)} />
              )}
            </div>
          </AccordionTab>
        </Accordion>

        <div className={styles.root_buttons}>
          <DefaultButton
            backgroundColor={generateHexFromString(buttonBackgroundColor)}
            textColor={generateHexFromString(buttonTextColor)}
            label={actionButtonLabel}
            width={width}
            type="submit"
          />
        </div>

        <WorkingHoursDialog
          visible={showWorkingTimeDialog}
          onHide={() => setShowWorkingTimeDialog(false)}
          submitEndpoint={endpointsMetadata.workingHours.saveWorkingHours}
          studioId={Number(id) ?? 0}
          disclaimer="Тази информация се използва само за информативна цел. Наличните слотове за резервации зависят от работното време на всеки служител."
          workingHoursData={workingHoursData}
          invalidateKeys={[
            [
              endpointsMetadata.studio.getStudio,
              endpointsMetadata.workingHours.getStudioWorkingHours,
            ],
          ]}
        />
      </div>
    </>
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSave)}>
        <SingleSectionPageLayout
          title={pageTitle}
          maxHeight={"90%"}
          children={children}
        />
      </form>
    </FormProvider>
  );
};
